import { put, delay, takeEvery, all, call, select, race, cancelled } from "redux-saga/effects";
import _ from 'lodash';
import moment from 'moment-timezone';
// import { getJwtToken, getDataFromKpiDash, getDataFromSxi } from "api";
import { getJwtToken, getDataFromRDS, getDataFromSxi, getDataFromKpiDash } from "api";
import { PICTREE, ENDPOINT } from "../../constants";

import {
  setPageTopLoader,
  setSeasonRankingData,
  setAreaRankingData
} from 'actions';

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,   // 30s
  retries: 3,         // 合計 4 回までトライ（初回 + リトライ3）
  backoffBase: 3000,  // 3s指数バックオフ
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,           // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2,                 // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 3000,          // 3秒
    backoffFactor = 2,           // 2倍ずつ
    jitter = true,               // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || '';
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    }
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功

    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
      }
    }
  }
}

export const dateRange = (state) => state.page.dateRangePicTree;
export const timeRange = (state) => state.page.timeRangePicTree;
export const selectedSeason = state => state.pictree.selectedSeason;

function* handleSeasonRankingData(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const timeRangeTarget = yield select(timeRange);
    const selectedSeasonData = yield select(selectedSeason);

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }

    const seasonQuery = (() => {
      if (selectedSeasonData.length > 0 && selectedSeasonData[0].seasonId != null) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `t.game_space_id = ${ids}`
          : `t.game_space_id IN (${ids})`;
      }
      return "1=1";
    })();

    const rangeStartUtc = moment
      .tz(`${rangeTarget[0]} ${timeRangeTarget[0]}:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    // UIの時刻入力は分単位なので、終端は次の分の先頭を排他的上限にして 23:59:59 まで含める
    const rangeEndUtcExclusive = moment
      .tz(`${rangeTarget[1]} ${timeRangeTarget[1]}:59`, "Asia/Tokyo")
      .add(1, "second")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const sqlQuerySeasonRanking = {
      query: `
        SELECT
            t.player_id,
            ps.name,
            t.team_code,
            t.amount,
            RANK() OVER (
              PARTITION BY t.team_code
              ORDER BY t.amount DESC
            ) AS ranking
        FROM
            total_rank_point_per_player AS t
        INNER JOIN
            player_state ps
            ON ps.player_id = t.player_id
        WHERE
            ${seasonQuery}
            AND t.amount > 0
            AND t.last_updated_datetime >= '${rangeStartUtc}'
            AND t.last_updated_datetime < '${rangeEndUtcExclusive}'
        ORDER BY
            t.amount DESC;
      `
    }

    const isTodayJP = moment(rangeTarget[0]).isSame(moment().tz('Asia/Tokyo').format('YYYY-MM-DD'), 'day');

    let dataRankingTemp;

    if (isTodayJP) {
      // 🔴 本日なら本番DB
      [dataRankingTemp] = yield all([
        call(getDataFromKpiDash, sqlQuerySeasonRanking, ENDPOINT.GET_DATA_API),
      ]);
    } else {
      // 🟢 本日以外はSXIレプリカ参照
      [dataRankingTemp] = yield all([
        //call(getDataFromSxi, sqlQuerySeasonRanking, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
        call(
          callWithTimeoutAndRetry,
          getDataFromRDS,
          [sqlQuerySeasonRanking, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        ),
      ]);
    }

    const rankingData = isTodayJP ? JSON.parse(dataRankingTemp.body) : dataRankingTemp;

    let overallRank = 0;
    let prevAmountOverall = null;
    let seenOverall = 0;

    const rankingTableData = rankingData.map((d) => {
      // overall
      seenOverall += 1;
      if (prevAmountOverall === null || d.amount !== prevAmountOverall) {
        overallRank = overallRank + 1;
        prevAmountOverall = d.amount;
      }
      const globalRank = overallRank;

      return [
        globalRank,        // 全体順位
        d.team_code,       // チーム
        d.ranking,         // チーム内順位
        d.name,            // プレイヤー名
        d.player_id,       // プレイヤーID
        d.amount           // 累計ランクポイント
      ];
    });
    yield put(setSeasonRankingData(rankingTableData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.error("Error fetching photo shoot ranking data:", error);
  }
}

function* handleAreaRankingData(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const timeRangeTarget = yield select(timeRange);
    const selectedSeasonData = yield select(selectedSeason);

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }

    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `t1.game_space_id = ${ids}` // ids は "'id1'"
          : `t1.game_space_id = IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const sqlQueryAreatRanking = {
      query: `
        SELECT
            n.name AS area_name,
            t1.player_id,
            ps.name AS player_name,
            t1.team_code,
            t1.amount,
            COUNT(*) AS ranking
        FROM
            total_rank_point_by_area_per_player AS t1
        INNER JOIN
            total_rank_point_by_area_per_player AS t2
            ON t1.team_code = t2.team_code
            AND t1.game_space_id = t2.game_space_id
            AND t1.amount <= t2.amount
            AND t1.area_id = t2.area_id
            AND (
                t2.amount > t1.amount
                OR (t2.amount = t1.amount AND t2.last_updated_datetime <= t1.last_updated_datetime)
            )
        INNER JOIN
            player_state ps
            ON ps.player_id = t1.player_id
        INNER JOIN
            game_space_area_name n
            ON n.area_id = t1.area_id AND n.language_code = 'JA'
        WHERE
            ${seasonQuery}
            AND t1.amount > 0
            AND CONVERT_TZ(t1.last_updated_datetime, 'UTC', 'Asia/Tokyo')
            BETWEEN '${rangeTarget[0]} ${timeRangeTarget[0]}:00' AND '${rangeTarget[1]} ${timeRangeTarget[1]}:00'
        GROUP BY
            n.name,
            t1.player_id,
            t1.team_code,
            t1.amount
        ORDER BY
            n.name,
            t1.team_code,
            t1.amount DESC,
            t1.last_updated_datetime ASC;
      `
    }

    const isTodayJP = moment(rangeTarget[0]).isSame(moment().tz('Asia/Tokyo').format('YYYY-MM-DD'), 'day');

    let dataRankingTemp;

    if (isTodayJP) {
      // 🔴 本日なら本番DB
      [dataRankingTemp] = yield all([
        call(getDataFromKpiDash, sqlQueryAreatRanking, ENDPOINT.GET_DATA_API),
      ]);
    } else {
      // 🟢 本日以外はSXIレプリカ参照
      [dataRankingTemp] = yield all([
        call(
          callWithTimeoutAndRetry,
          getDataFromRDS,
          [sqlQueryAreatRanking, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        ),
      ]);
    }

    const rankingData = isTodayJP ? JSON.parse(dataRankingTemp.body) : dataRankingTemp;
    const grouped = _.groupBy(rankingData, (d) => `${d.area_name}__${d.team_code}`);

    const groupKeysSorted = _.sortBy(Object.keys(grouped), (k) => {
      const [area, team] = k.split('__');
      return `${area}__${team}`;
    });

    let rankingTableData = [];

    groupKeysSorted.forEach((key) => {
      const rows = grouped[key];

      // amount DESC、同点は player_name ASC → player_id ASC くらいで安定化
      const sorted = _.orderBy(rows, ['amount', 'player_name', 'player_id'], ['desc', 'asc', 'asc']);

      sorted.forEach((d, idx) => {
        rankingTableData.push([
          idx + 1,           // 順
          d.area_name,       // エリア名
          d.player_id,       // プレイヤーID
          d.player_name,     // プレイヤー名
          d.team_code,       // チーム
          d.amount           // 累計ランクポイント（テーブル側で桁区切り表示）
        ]);
      });
    });

    yield put(setAreaRankingData(rankingTableData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.error("Error fetching photo shoot ranking data:", error);
  }
}

// エリア別ランキング用 Saga
function* handleAreaRankingData2(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken           = yield call(getJwtToken);
    const rangeTarget        = yield select(dateRange);
    const timeRangeTarget    = yield select(timeRange);
    const selectedSeasonData = yield select(selectedSeason);

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }

    // game_space_id 句の整備（= IN の誤りも修正）
    const seasonQuery = (() => {
      if (selectedSeasonData.length > 0 && selectedSeasonData[0].seasonId != null) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `t1.game_space_id = ${ids}`
          : `t1.game_space_id IN (${ids})`;
      }
      return "1=1";
    })();

    // ── SQL：順位ロジックは載せない（取得だけ）
    const sqlQueryAreaRanking = {
      query: `
        SELECT
          n.name AS area_name,
          t1.player_id,
          ps.name AS player_name,
          t1.team_code,
          t1.amount,
          t1.last_updated_datetime
        FROM total_rank_point_by_area_per_player AS t1
        JOIN player_state ps ON ps.player_id = t1.player_id
        JOIN game_space_area_name n
          ON n.area_id = t1.area_id AND n.language_code = 'JA'
        WHERE
          ${seasonQuery}
          AND t1.amount > 0
          AND CONVERT_TZ(t1.last_updated_datetime, 'UTC', 'Asia/Tokyo')
              BETWEEN '${rangeTarget[0]} ${timeRangeTarget[0]}:00'
                  AND '${rangeTarget[1]} ${timeRangeTarget[1]}:00'
        ORDER BY
          n.name ASC,            -- エリア名順
          t1.amount DESC,        -- ポイント降順
          ps.name ASC,
          t1.player_id ASC
      `
    };

    const isTodayJP = moment(rangeTarget[0]).isSame(
      moment().tz('Asia/Tokyo').format('YYYY-MM-DD'), 'day'
    );

    let dataRankingTemp;
    if (isTodayJP) {
      [dataRankingTemp] = yield all([
        call(getDataFromKpiDash, sqlQueryAreaRanking, ENDPOINT.GET_DATA_API),
      ]);
    } else {
      [dataRankingTemp] = yield all([
        call(
          callWithTimeoutAndRetry,
          getDataFromRDS,
          [sqlQueryAreaRanking, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
          SXI_HEAVY_RETRY
        ),
      ]);
    }

    const rows = isTodayJP ? JSON.parse(dataRankingTemp.body) : dataRankingTemp;

    // ── JSで順位付け（同点は同順位：DENSE_RANK）
    // 1) エリアごとにグループ化
    const byArea = _.groupBy(rows, (d) => d.area_name);

    // 出力：「エリア名」「全体順位」「チーム」「チーム内順位」「プレイヤー名」「プレイヤーID」「累計ランクポイント」
    const table = [];

    Object.keys(byArea).sort().forEach((area) => {
      // エリア内の並び：amount DESC, name ASC, id ASC（SQL側と一致させる）
      const sorted = _.orderBy(byArea[area],
        ['amount', 'player_name', 'player_id'],
        ['desc',   'asc',         'asc']
      );

      // エリア内の全体順位（dense）
      let prevAmount = null;
      let overallRank = 0;

      // チーム別の進行状況
      const teamProg = {}; // { [team_code]: { prevAmount, rank } }

      sorted.forEach((d) => {
        // overall dense rank
        if (prevAmount === null || d.amount !== prevAmount) {
          overallRank += 1;
          prevAmount = d.amount;
        }
        const areaOverallRank = overallRank;

        // team dense rank inside area
        if (!teamProg[d.team_code]) teamProg[d.team_code] = { prevAmount: null, rank: 0 };
        const tp = teamProg[d.team_code];
        if (tp.prevAmount === null || d.amount !== tp.prevAmount) {
          tp.rank += 1;
          tp.prevAmount = d.amount;
        }
        const teamRank = tp.rank;

        table.push([
          area,                // エリア名
          areaOverallRank,     // 全体順位（エリア内）
          d.team_code,         // チーム
          teamRank,            // チーム内順位（エリア×チーム）
          d.player_name,       // プレイヤー名
          d.player_id,         // プレイヤーID
          d.amount             // 累計ランクポイント
        ]);
      });
    });

    yield put(setAreaRankingData(table));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.error("Error fetching area ranking data:", error);
  }
}


export default function* watchMapSettings() {
  yield takeEvery(PICTREE.GET_SEASON_RANKING_DATA, handleSeasonRankingData);
  yield takeEvery(PICTREE.GET_AREA_RANKING_DATA, handleAreaRankingData2);
}
